import React, { useMemo } from "react";
import { formatCurrency } from "../../utils/helpers";
import {
  allocateGlAmounts,
  areGlSharesComplete,
  formatGlCodeLabel,
  glCodesForDropdown,
  glShareTotal,
  newGlAllocation,
  parseSharePercent,
} from "../../utils/glCodes";

import CustomSelect from "../ui/CustomSelect";
const fieldClass =
  "w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400 focus:border-stone-400 disabled:bg-gray-50 disabled:cursor-not-allowed";

function formatAmount(amount) {
  if (amount == null || Number.isNaN(amount)) return "—";
  return formatCurrency(amount);
}

const InvoiceGlAllocationEditor = ({
  glCodes = [],
  allocations = [],
  onAllocationsChange,
  splitEnabled,
  onSplitChange,
  totalAmount,
  disabled = false,
  className,
}) => {
  const amounts = useMemo(() => {
    const total = parseFloat(totalAmount);
    return allocateGlAmounts(
      Number.isFinite(total) ? total : NaN,
      allocations.map((row) => parseSharePercent(row.share)),
    );
  }, [allocations, totalAmount]);

  const shareTotal = glShareTotal(allocations);
  const complete =
    areGlSharesComplete(allocations) &&
    allocations.every((row) => String(row.gl_code || "").trim());
  const allocatedAmount = amounts.every((amount) => amount != null)
    ? amounts.reduce((sum, amount) => sum + amount, 0)
    : null;

  const updateRow = (index, patch) => {
    onAllocationsChange(
      allocations.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    );
  };

  const addRow = () => {
    const next = allocations.map((row) => ({ ...row }));
    if (next.length === 1 && parseSharePercent(next[0].share) === 100) {
      next[0] = { ...next[0], share: "" };
    }
    next.push(newGlAllocation("", ""));
    onAllocationsChange(next);
    if (!splitEnabled && typeof onSplitChange === "function") onSplitChange(true);
  };

  const removeRow = (index) => {
    if (allocations.length <= 1) return;
    const next = allocations.filter((_, rowIndex) => rowIndex !== index);
    if (next.length === 1 && !parseSharePercent(next[0].share)) {
      next[0] = { ...next[0], share: "100" };
    }
    onAllocationsChange(next);
    if (next.length === 1 && typeof onSplitChange === "function") {
      onSplitChange(false);
    }
  };

  const toggleSplit = (checked) => {
    if (typeof onSplitChange === "function") onSplitChange(checked);
    if (checked) {
      if (allocations.length === 0) onAllocationsChange([newGlAllocation("", "100")]);
      return;
    }
    onAllocationsChange([
      newGlAllocation(allocations[0]?.gl_code || "", "100"),
    ]);
  };

  const usedCodes = new Set(
    allocations.map((row) => String(row.gl_code || "").trim().toLowerCase()).filter(Boolean),
  );

  return (
    <div className={className != null ? className : "mt-6"}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <label className="block text-sm font-medium text-gray-700">
          Account code (GL) <span className="text-red-500">*</span>
          {splitEnabled ? (
            <span className="font-normal text-gray-500"> split across codes</span>
          ) : null}
        </label>
        {splitEnabled ? (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
              complete
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-800"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                complete ? "bg-emerald-500" : "bg-amber-500"
              }`}
            />
            {complete
              ? `100% allocated${allocatedAmount != null ? ` - ${formatAmount(allocatedAmount)}` : ""}`
              : `${roundDisplay(shareTotal)}% allocated`}
          </span>
        ) : null}
      </div>

      <label className="mb-3 inline-flex items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={splitEnabled}
          onChange={(event) => toggleSplit(event.target.checked)}
          disabled={disabled}
          className="rounded border-stone-300 text-stone-800 focus:ring-stone-500"
        />
        Split this invoice across multiple GL codes
      </label>

      {splitEnabled ? (
        <p className="mb-4 text-sm text-gray-500">
          Each line is coded separately and sent to Accounting. The percentages
          must add up to 100%, and the amount is calculated from the total
          invoice amount below.
        </p>
      ) : null}

      <div className="space-y-3">
        {splitEnabled && (
          <div className="hidden sm:grid sm:grid-cols-[minmax(0,1fr)_7rem_9rem_4.5rem] gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-0.5">
            <span>GL code</span>
            <span>Share</span>
            <span>Amount</span>
            <span className="sr-only">Remove</span>
          </div>
        )}
        {allocations.map((row, index) => {
          const options = glCodesForDropdown(glCodes, row.gl_code).filter(
            (code) =>
              String(code.gl_code).toLowerCase() ===
                String(row.gl_code || "").toLowerCase() ||
              !usedCodes.has(String(code.gl_code).toLowerCase()),
          );
          const selected = options.some(
            (code) => String(code.gl_code) === String(row.gl_code || ""),
          )
            ? String(row.gl_code || "")
            : "";
          return (
            <div
              key={row.client_id || index}
              className={
                splitEnabled
                  ? "grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_7rem_9rem_4.5rem] gap-2 items-center"
                  : ""
              }
            >
              <CustomSelect
                value={selected}
                onChange={(event) => updateRow(index, { gl_code: event.target.value })}
                disabled={disabled}
                className={fieldClass}
              >
                <option value="">Select GL code</option>
                {options.map((code) => (
                  <option key={code.gl_code} value={code.gl_code}>
                    {formatGlCodeLabel(code.gl_code, code.description)}
                  </option>
                ))}
              </CustomSelect>
              {splitEnabled ? (
                <>
                  <div className="relative">
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      min="0"
                      max="100"
                      value={row.share}
                      onChange={(event) => updateRow(index, { share: event.target.value })}
                      onWheel={(event) => event.target.blur()}
                      disabled={disabled}
                      className={`${fieldClass} pr-8 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                      %
                    </span>
                  </div>
                  <div className={`${fieldClass} bg-gray-50 text-gray-700`}>
                    {formatAmount(amounts[index])}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    disabled={disabled || allocations.length <= 1}
                    className="text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed text-left sm:text-right"
                  >
                    Remove
                  </button>
                </>
              ) : null}
            </div>
          );
        })}
      </div>

      {splitEnabled ? (
        <button
          type="button"
          onClick={addRow}
          disabled={disabled}
          className="mt-3 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Add GL code
        </button>
      ) : null}
    </div>
  );
};

function roundDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n * 100) / 100);
}

export default InvoiceGlAllocationEditor;
